import useAdminAccess from "hooks/useAdminAccess";
import AdminPermission from "components/admin/AdminPermission";
import AdminSidebar from "components/layout/AdminSidebar";
import Container from "components/layout/Container";
import Main from "components/layout/Main";
import Spinner from "components/ui/Spinner";
import VButton from "components/ui/VButton";
import {
  COIN_CONFIG,
  COIN_CONSTANTS,
  type ChainNetworkInfo,
} from "constants/coin.constant";
import { DateTime } from "luxon";
import { useState } from "react";
import { useQueries } from "react-query";
import { cryptoFormat } from "utilities/currency";
import { showDanger, showSuccess } from "utilities/misc";
import { get, post } from "utilities/network";

const coinKeys = Object.keys(COIN_CONFIG).filter(
  (key) => key !== COIN_CONSTANTS.BTC,
) as (keyof typeof COIN_CONFIG)[];

const BtcBalance: React.FC = () => {
  const { can } = useAdminAccess();
  const coinQueries = useQueries(
    coinKeys.map((key) => ({
      queryKey: ["coinBalance", key],
      enabled: can("analytics.coinBalance.view"),
      queryFn: () => get(`admin/analytics/coinBalance?crypto=${key}`),
    })),
  );

  const [sell, setSell] = useState({
    btc: 0,
    usd: 0,
    blockchainTotal: 0,
    blockchainTotalUsd: 0,
  });
  const [receive, setReceive] = useState({
    btc: 0,
    usd: 0,
    blockchainTotal: 0,
    blockchainTotalUsd: 0,
  });
  const [web, setWeb] = useState({
    btc: 0,
    usd: 0,
    blockchainTotal: 0,
    blockchainTotalUsd: 0,
  });
  const [usdtBalances, setUsdtBalances] = useState<any>({
    balance: 0,
    lastUpdated: DateTime.now().toISO(),
    btc: {},
  });
  const [isLoading, setLoading] = useState(false);

  /// Bump BTC Fee
  const [hash, setHash] = useState("");
  const [fee, setFee] = useState("");
  const [bumpLoading, setBumpLoading] = useState(false);

  const handleBump = async () => {
    if (!hash || !fee) return showDanger("Enter valid hash and fee");
    setBumpLoading(true);
    try {
      let result = await post(`admin/transactions/bumpBtcFee`, {
        hash,
        fee,
      });
      if (result?.data?.success) {
        showSuccess(result?.data?.message || "Hash bumped successfully");
        setHash("");
        setFee("");
      }
      setBumpLoading(false);
    } catch (e) {
      console.log(e);
      setBumpLoading(false);
      showDanger("Something went wrong");
    }
  };

  const handleFetch = async (type: string) => {
    setLoading(true);
    try {
      let result = await get(`admin/analytics/btcBalance?which=${type}`);
      setLoading(false);
      if (type === "sell") {
        setSell(result);
      } else if (type === "receive") {
        setReceive(result);
      } else if (type === "usdtBalances") {
        setUsdtBalances(result);
      } else {
        setWeb(result);
      }
    } catch (e) {
      console.log(e);
      setLoading(false);
      showDanger("Something went wrong");
    }
  };

  return (
    <Container>
      <AdminSidebar />
      <Main className="mx-5 mt-5 text-left">
        <section className="flex justify-between-center">
          <div>
            <h2 className="text-xl">Bitcoin Balance</h2>
            <div className="flex gap-8 mt-14">
              <div className="flex">
                <div className="gap-5">
                  <div className="relative mr-5">
                    <label>Sell Balance</label>
                    <h3 className="mt-2">
                      BTC:{" "}
                      <span className="text-xl">
                        {sell.btc || usdtBalances?.btc?.sell?.btc || 0}
                      </span>
                    </h3>
                    <h3 className="mt-2">
                      USD:{" "}
                      <span className="text-xl">
                        {sell.usd || usdtBalances?.btc?.sell?.usd || 0}
                      </span>
                    </h3>

                    <h3 className="pt-2 mt-2">
                      Onchain BTC:{" "}
                      <span className="text-xl">
                        {sell.blockchainTotal ||
                          usdtBalances?.btc?.sell?.blockchainTotal ||
                          0}
                      </span>
                    </h3>
                    <h3 className="mt-2">
                      Onchain USD:{" "}
                      <span className="text-xl">
                        {sell.blockchainTotalUsd ||
                          usdtBalances?.btc?.sell?.blockchainTotalUsd ||
                          0}
                      </span>
                    </h3>
                    <h3 className="mt-2">
                      Last Updated :{" "}
                      <span className="text-lg">
                        {usdtBalances?.btc?.sell?.lastUpdated ? (
                          <span>
                            {DateTime.fromISO(
                              usdtBalances?.btc?.sell?.lastUpdated,
                            ).toFormat("DD")}
                            &nbsp;
                            {DateTime.fromISO(
                              usdtBalances?.btc?.sell?.lastUpdated,
                            ).toFormat("t")}
                          </span>
                        ) : null}
                      </span>
                    </h3>
                  </div>

                  <VButton
                    status={isLoading ? "pending" : ""}
                    onClick={() => handleFetch("sell")}
                    className="w-5/12 px-20 py-3 mt-5 font-light text-white bg-black rounded-lg"
                  >
                    Refetch
                  </VButton>
                </div>

                <div className="gap-5 mr-5">
                  <div className="relative">
                    <label>Receive Balance</label>

                    <h3 className="mt-2">
                      BTC:{" "}
                      <span className="text-xl">
                        {receive.btc || usdtBalances?.btc?.receive?.btc || 0}
                      </span>
                    </h3>
                    <h3 className="mt-2">
                      USD:{" "}
                      <span className="text-xl">
                        {receive.usd || usdtBalances?.btc?.receive?.usd || 0}
                      </span>
                    </h3>

                    <h3 className="pt-2 mt-2">
                      Onchain BTC:{" "}
                      <span className="text-xl">
                        {receive.blockchainTotal ||
                          usdtBalances?.btc?.receive?.blockchainTotal ||
                          0}
                      </span>
                    </h3>
                    <h3 className="mt-2">
                      Onchain USD:{" "}
                      <span className="text-xl">
                        {receive.blockchainTotalUsd ||
                          usdtBalances?.btc?.receive?.blockchainTotalUsd ||
                          0}
                      </span>
                    </h3>
                    <h3 className="mt-2">
                      Last Updated :{" "}
                      <span className="text-lg">
                        {usdtBalances?.btc?.receive?.lastUpdated ? (
                          <span>
                            {DateTime.fromISO(
                              usdtBalances?.btc?.receive?.lastUpdated,
                            ).toFormat("DD")}
                            &nbsp;
                            {DateTime.fromISO(
                              usdtBalances?.btc?.receive?.lastUpdated,
                            ).toFormat("t")}
                          </span>
                        ) : null}
                      </span>
                    </h3>
                  </div>

                  <VButton
                    status={isLoading ? "pending" : ""}
                    onClick={() => handleFetch("receive")}
                    className="w-5/12 px-20 py-3 mt-5 font-light text-white bg-black rounded-lg"
                  >
                    Refetch
                  </VButton>
                </div>

                <div className="gap-5">
                  <div className="relative">
                    <label>Web Balance</label>

                    <h3 className="mt-2">
                      BTC: <span className="text-xl">{web.btc}</span>
                    </h3>
                    <h3 className="mt-2">
                      USD: <span className="text-xl">{web.usd}</span>
                    </h3>

                    <h3 className="pt-2 mt-2">
                      Onchain BTC:{" "}
                      <span className="text-xl">{web.blockchainTotal}</span>
                    </h3>
                    <h3 className="mt-2">
                      Onchain USD:{" "}
                      <span className="text-xl">{web.blockchainTotalUsd}</span>
                    </h3>
                  </div>

                  <VButton
                    status={isLoading ? "pending" : ""}
                    onClick={() => handleFetch("web")}
                    className="w-5/12 px-20 py-3 mt-5 font-light text-white bg-black rounded-lg"
                  >
                    Refetch
                  </VButton>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h2 className="mb-3 text-xl">Bump BTC Fee</h2>
            <div className="flex gap-5">
              <div className="relative w-6/12">
                <label>Hash</label>
                <input
                  type="text"
                  name="hash"
                  value={hash}
                  onChange={(e) => setHash(e.target.value)}
                  className="w-full py-3 pl-8 mt-1 border-gray-400 rounded"
                />
              </div>
              <div className="relative w-3/12">
                <label>Fee</label>
                <input
                  type="number"
                  className="w-full py-3 pl-8 mt-1 border-gray-400 rounded"
                  name="fee"
                  value={fee}
                  onChange={(e) => setFee(e.target.value)}
                />
              </div>
              <div className="w-3/12 text-center">
                <AdminPermission permission={"transactions.fees.edit"}>
                  <VButton
                    status={bumpLoading ? "pending" : ""}
                    onClick={handleBump}
                    className="px-20 py-3 mt-8 font-light text-white bg-black rounded-lg"
                  >
                    Bump
                  </VButton>
                </AdminPermission>
              </div>
            </div>
          </div>
        </section>

        <AdminPermission permission={"analytics.coinBalance.view"}>
          <section className="flex flex-col gap-10 mt-16 mb-5">
            {coinKeys.map((coinKey, index) => {
              const query = coinQueries[index];
              const data = (query?.data || {}) as {
                networks?: Record<
                  string,
                  { balance?: number; usd?: number; lastUpdated?: string }
                >;
              };
              const coinConfig = COIN_CONFIG[coinKey];
              const networkEntries = Object.entries(coinConfig.networks) as [
                string,
                ChainNetworkInfo,
              ][];

              return (
                <div key={coinKey} className="flex flex-col gap-3">
                  <h2 className="text-xl font-medium">
                    {coinKey.toUpperCase()} Address Balance
                  </h2>
                  <div className="flex flex-wrap gap-4">
                    {networkEntries.map(([networkKey, networkConfig], colIndex) => {
                      const networkData = data.networks?.[networkKey];
                      const networkName = networkConfig?.name || networkKey.toUpperCase();

                      return (
                        <div
                          key={networkKey}
                          className={`min-w-[200px] flex-1 rounded-lg px-5 py-4 ${
                            colIndex % 2 === 0
                              ? "bg-black text-white"
                              : "border-2 border-black bg-white text-black"
                          }`}
                        >
                          <h4 className="mb-2 text-base font-normal">{networkName}</h4>
                          {query.isLoading ? (
                            <div className="flex items-center justify-center py-2">
                              <Spinner className="text-lg" />
                            </div>
                          ) : (
                            <>
                              <p className="text-sm font-light">
                                <span>Balance:&nbsp;</span>
                                <span>{cryptoFormat(networkData?.balance ?? 0)}</span>
                              </p>
                              <p className="text-sm font-light">
                                <span>USD Value:&nbsp;</span>
                                <span>{cryptoFormat(networkData?.usd ?? 0)}</span>
                              </p>
                              {networkData?.lastUpdated && (
                                <p className="mt-1 text-xs font-light">
                                  <span>Last updated:&nbsp;</span>
                                  <span className="font-thin">
                                    {DateTime.fromISO(networkData.lastUpdated).toFormat("DD")}
                                    &nbsp;
                                    {DateTime.fromISO(networkData.lastUpdated).toFormat("t a")}
                                  </span>
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </section>
        </AdminPermission>
      </Main>
    </Container>
  );
};
export default BtcBalance;
